import { useTranslation } from 'react-i18next';
import { queryClient } from '@/common/contexts/react-query/ReactQueryProvider';
import { MENHIR_ENDPOINTS } from '@/common/integrations/menhir/menhir.endpoints';
import { notify } from '@/common/utils/toast/toast';
import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {
  addInsuranceCoveredMember,
  confirmHealthInsurancePlan,
  createPlanConfirmation,
  createPlanEligibility,
  createPlanSimulation,
  getAvailableOptions,
  getInsuranceCoveredMember,
  getInsuranceDuplicatedFamilyMembers,
  getInsuranceNotCoveredMembers,
  getInsurancePolicies,
  getInsurancePolicy,
  getInsurancePolicyOperator,
  getInsurncePayrollMovements,
  getOptInEligibility,
  getSelectedPlanEstimations,
  getUpgradeOptions,
  removeFromPolicy,
  updateInsuranceCoveredMemberPayment,
  upgradeInsurancePlan,
} from './insurance.services';
import {
  IInsuranceCoveredMember,
  IInsurancePayrollMovement,
  IInsurancePolicies,
  IInsurancePolicy,
  InsurancePaymentMethod,
  TInsuranceAvailablePlan,
  TInsuranceDuplicatedMember,
  TInsurancePlanSimulation,
  TInsurancePolicyOperator,
  TInsuranceUpgradePlan,
  TMemberEligibility,
  TMenhirMemberEligibility,
  TMenhirPlanConfirmation,
  TMenhirPlanSimulation,
} from './insurance.types';

export function useGetInsurancePolicies(
  options?: UseQueryOptions<IInsurancePolicies>
) {
  const { data: policies, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.ROOT],
    queryFn: getInsurancePolicies,
    ...options,
  });

  return {
    policies: policies?.activePolicies,
    upcomingPolicy: policies?.upcomingPolicy,
    ...rest,
  };
}

export function useGetInsurancePolicy(
  policyId: string,
  options?: UseQueryOptions<IInsurancePolicy>
) {
  const { data: policy, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.POLICY, policyId],
    queryFn: () => getInsurancePolicy(policyId),
    ...options,
  });

  return { policy, ...rest };
}

export function useGetInsuranceCoveredMember(
  { policyId, memberId }: { policyId: string; memberId: string },
  options?: UseQueryOptions<IInsuranceCoveredMember>
) {
  const { data: coveredMember, ...rest } = useQuery({
    queryKey: [
      MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.COVERED_MEMBER,
      policyId,
      memberId,
    ],
    queryFn: () => getInsuranceCoveredMember(policyId, memberId),
    ...options,
  });
  return { coveredMember, ...rest };
}

export function useUpdateInsuranceCoveredMemberPayment(
  options?: UseMutationOptions<
    any,
    unknown,
    {
      policyId: string;
      memberId: string;
      paymentMethod: InsurancePaymentMethod;
    }
  >
) {
  return useMutation({
    mutationFn: ({ policyId, memberId, paymentMethod }) =>
      updateInsuranceCoveredMemberPayment(policyId, memberId, paymentMethod),
    onSuccess: (_data, { policyId, memberId }) => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.ROOT,
      ]);
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.COVERED_MEMBER,
        policyId,
        memberId,
      ]);
    },
    ...options,
  });
}

export function useAddInsuranceCoveredMember(
  options?: UseMutationOptions<
    any,
    unknown,
    Parameters<typeof addInsuranceCoveredMember>[0]
  >
) {
  return useMutation({
    mutationFn: (args) => addInsuranceCoveredMember(args),
    onSuccess: (_data, { policyId }) => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.NOT_COVERED_MEMBERS,
        policyId,
      ]);
      queryClient.removeQueries([MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.ROOT]);
    },
    ...options,
  });
}

export function useRemoveFromPolicy(
  options?: UseMutationOptions<any, unknown, { policyId: string }>
) {
  return useMutation({
    mutationFn: ({ policyId }) => removeFromPolicy({ policyId }),
    onSuccess: (_data, { policyId }) => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.ROOT,
      ]);
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.NOT_COVERED_MEMBERS,
        policyId,
      ]);
    },
    ...options,
  });
}

export function useGetInsuranceNotCoveredMembers(policyId: string) {
  const { data: notCoveredMembers, ...rest } = useQuery({
    queryKey: [
      MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.NOT_COVERED_MEMBERS,
      policyId,
    ],
    queryFn: () => getInsuranceNotCoveredMembers(policyId),
  });

  return { notCoveredMembers, ...rest };
}

export function useGetInsuranceDuplicatedFamilyMembers(
  options?: UseQueryOptions<TInsuranceDuplicatedMember[]>
) {
  const { data: duplicatedFamilyMembers, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.DUPLICATED_FAMILY_MEMBERS],
    queryFn: getInsuranceDuplicatedFamilyMembers,
    ...options,
  });
  return {
    duplicatedFamilyMembers,
    ...rest,
  };
}

export function useGetInsuranceUpgradeOptions(
  options?: UseQueryOptions<TInsuranceUpgradePlan[]>
) {
  const { data: plans, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.UPGRADE_OPTIONS],
    queryFn: getUpgradeOptions,
    ...options,
  });
  return { plans, ...rest };
}

export function useUpgradeInsurancePlan(
  options?: UseMutationOptions<
    unknown,
    unknown,
    Parameters<typeof upgradeInsurancePlan>[0]
  >
) {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: upgradeInsurancePlan,
    onSuccess: () => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.UPGRADE_OPTIONS,
      ]);
      queryClient.invalidateQueries([MENHIR_ENDPOINTS.EMPLOYEE.SPOTLIGHTS]);
      queryClient.removeQueries([MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.ROOT]);
      notify({
        message: t('dashboard.insurance.upgrade_plan.on_success_message'),
        type: 'success',
      });
    },
    onError: () => {
      notify({
        message: t('dashboard.insurance.upgrade_plan.on_error_message'),
        type: 'error',
      });
    },
    ...options,
  });
}

export function useGetInsurancePayrollMovements(
  policyId: string,
  options?: UseQueryOptions<IInsurancePayrollMovement[]>
) {
  const { data: payrollMovements, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.PAYROLL_PAYMENTS, policyId],
    queryFn: () => getInsurncePayrollMovements(policyId),
    ...options,
  });

  return { payrollMovements, ...rest };
}

export function useGetInsuranceAvailableOptions(
  options?: UseQueryOptions<TInsuranceAvailablePlan[], any>
) {
  const {
    data: plans,
    error,
    ...rest
  } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.AVAILABLE_OPTIONS],
    queryFn: getAvailableOptions,
    ...options,
  });
  return { plans: plans || [], error, ...rest };
}

export function useInsuranceOptInEligibility(
  options?: UseQueryOptions<boolean>
) {
  const { data, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.OPT_IN_ELIGIBILTY],
    queryFn: getOptInEligibility,
    ...options,
  });
  return { eligible: data, ...rest };
}

export function useGetSelectedPlanEstimations(
  personsIds: string[],
  policyId: string,
  options?: UseQueryOptions<TInsuranceAvailablePlan>
) {
  const { data: plan, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.PLAN_ESTIMATIONS],
    queryFn: () => getSelectedPlanEstimations({ personsIds, policyId }),
    ...options,
  });
  return { plan, ...rest };
}

export function useConfirmHealthInsurancePlan(
  options?: UseMutationOptions<
    unknown,
    unknown,
    Parameters<typeof confirmHealthInsurancePlan>[0]
  >
) {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: confirmHealthInsurancePlan,
    onSuccess: () => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.AVAILABLE_OPTIONS,
      ]);
      queryClient.removeQueries([MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.ROOT]);
      notify({
        message: t('dashboard.insurance.upgrade_plan.on_success_message'),
        type: 'success',
      });
    },
    onError: () => {
      notify({
        message: t('dashboard.insurance.upgrade_plan.on_error_message'),
        type: 'error',
      });
    },
    ...options,
  });
}

export function useCreatePlanSimulation(
  options?: UseMutationOptions<
    TInsurancePlanSimulation,
    unknown,
    TMenhirPlanSimulation
  >
) {
  return useMutation({
    mutationFn: ({ policyId, persons }) =>
      createPlanSimulation({ policyId, persons }),
    ...options,
  });
}

export function useCreatePlanEligibility(
  options?: UseMutationOptions<
    TMemberEligibility[],
    unknown,
    {
      policyId: string;
      persons: TMenhirMemberEligibility[];
    }
  >
) {
  return useMutation({
    mutationFn: ({ policyId, persons }) =>
      createPlanEligibility({ policyId, persons }),
    ...options,
  });
}

export function useCreatePlanConfirmation(
  options?: UseMutationOptions<any, unknown, TMenhirPlanConfirmation>
) {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: createPlanConfirmation,
    onSuccess: () => {
      notify({
        message: t(
          'dashboard.insurance_detail.add_family_members.confirm.success.message'
        ),
        type: 'success',
      });
      queryClient.invalidateQueries([MENHIR_ENDPOINTS.EMPLOYEE.SPOTLIGHTS]);
      queryClient.removeQueries([MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.ROOT]);
    },
    ...options,
  });
}

export function useGetInsurancePolicyOperator(
  policyId: string,
  options?: UseQueryOptions<TInsurancePolicyOperator>
) {
  const { data: operator, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.INSURANCES.POLICY_OPERATOR, policyId],
    queryFn: () => getInsurancePolicyOperator(policyId),
    ...options,
  });

  return { operator, ...rest };
}
